import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getLocationTypeById, deleteLocationType, getLocationTypes, getLocTypeServiceTypes, getLocTypeTopicTypes } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import {
  Tag,
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  ChevronUp,
  ChevronDown,
  Layers,
  BookOpen
} from 'lucide-react';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';
import ServiceTypesReadOnlyView from '../components/ServiceTypesReadOnlyView';
import TopicTypesReadOnlyView from '../components/TopicTypesReadOnlyView';

export default function ViewLocationTypePage() {
  const { locationTypeId } = useParams<{ locationTypeId: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteLocationTypeFn = useAction(deleteLocationType);
  const [activeTab, setActiveTab] = useState<'details' | 'services' | 'topics'>('details');

  // Fetch location type data
  const {
    data: locationType,
    isLoading,
    error
  } = useQuery(getLocationTypeById, { id: locationTypeId! });

  // Fetch all location types for navigation
  const { data: allLocationTypes = [] } = useQuery(getLocationTypes);

  // Fetch linked service types and topic types (read-only)
  const { data: locTypeServiceTypes = [] } = useQuery(getLocTypeServiceTypes, { locationTypeId: locationTypeId! });
  const { data: locTypeTopicTypes = [] } = useQuery(getLocTypeTopicTypes, { locationTypeId: locationTypeId! });

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    navigate('/admin/taxonomy/location-categories');
  };

  // Find current index and get prev/next location types
  const currentIndex = (allLocationTypes as any[]).findIndex((type: any) => type.id === locationTypeId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allLocationTypes as any[]).length - 1;
  const prevLocationType = hasPrev ? (allLocationTypes as any[])[currentIndex - 1] : null;
  const nextLocationType = hasNext ? (allLocationTypes as any[])[currentIndex + 1] : null;

  const handleDelete = async () => {
    if (!locationType) return;
    if (!confirm(`Are you sure you want to delete "${locationType.label}"?`)) return;

    try {
      await deleteLocationTypeFn({ id: locationType.id });
      toast({
        title: "Success",
        description: "Location type deleted successfully"
      });
      navigate('/admin/taxonomy/location-categories');
    } catch (err: any) {
      console.error('Error deleting location type:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete location type",
        variant: "destructive"
      });
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">You must be logged in to view location types.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading location type...</p>
        </div>
      </div>
    );
  }

  if (error || !locationType) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tag className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Location Category Not Found</h3>
          <p className="text-gray-600 mb-4">The location type you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Location Categories
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <div className="space-y-3">
          {/* Navigation Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => prevLocationType && navigate(`/admin/taxonomy/location-categories/${prevLocationType.id}`)}
              disabled={!hasPrev}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasPrev ? `Previous: ${prevLocationType?.label}` : 'No previous item'}
            >
              <ChevronUp className="w-4 h-4" />
              <span className="text-sm font-medium">Up</span>
            </button>
            <button
              onClick={() => nextLocationType && navigate(`/admin/taxonomy/location-categories/${nextLocationType.id}`)}
              disabled={!hasNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasNext ? `Next: ${nextLocationType?.label}` : 'No next item'}
            >
              <span className="text-sm font-medium">Down</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButtonLink
              to={`/admin/taxonomy/location-categories/${locationType.id}/edit`}
            >
              Edit
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
            >
              Delete
            </ActionButtonDanger>
          </ActionButtons>
        </div>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Key" value={locationType.key} />
          <DetailField label="Status" value={locationType.isActive ? 'Active' : 'Inactive'} />
          <DetailField label="Created" value={formatDate(locationType.createdAt)} />
          <DetailField label="Last Updated" value={formatDate(locationType.updatedAt)} />
          <DetailField label="Locations" value={locationType._count?.locations || 0} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={[
        { label: 'Admin', href: '/admin' },
        { label: 'Location Categories', href: '/admin/taxonomy/location-categories', isClickable: true }
      ]}
      currentPageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      {/* Location Type Header */}
      <div className="mb-8">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{locationType.label}</h1>
            </div>

            <p className="text-lg text-gray-600 font-mono mb-4">{locationType.key}</p>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 ml-4">
            {locationType.isActive ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-green-100 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-100 text-red-800">
                <XCircle className="w-4 h-4" />
                <span className="font-medium">Inactive</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Created {formatDate(locationType.createdAt)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Updated {formatDate(locationType.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'details'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Tag className="w-4 h-4" />
          Details
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'services'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Layers className="w-4 h-4" />
          Services
        </button>
        <button
          onClick={() => setActiveTab('topics')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'topics'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Topics
        </button>
      </div>

      {activeTab === 'services' && (
        <ServiceTypesReadOnlyView linkedServiceTypes={locTypeServiceTypes as any[]} />
      )}

      {activeTab === 'topics' && (
        <TopicTypesReadOnlyView linkedTopicTypes={locTypeTopicTypes as any[]} />
      )}

      {/* Location Type Content */}
      {activeTab === 'details' && <div className="space-y-6">
        {/* Type Badge */}
        <div className="flex flex-wrap gap-3 mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-700">
            <Tag className="w-5 h-5" />
            <span className="font-medium">{locationType.key}</span>
          </span>
        </div>

        {/* Description */}
        {locationType.description && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
            <div className="prose prose-gray max-w-none">
              <p className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {locationType.description}
              </p>
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Key</p>
                <p className="font-medium text-gray-900 font-mono">{locationType.key}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-gray-600">Locations Using This Type</p>
                <p className="font-medium text-gray-900">{locationType._count?.locations || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Locations Using This Type */}
        {locationType.locations && locationType.locations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Locations Using This Type
            </h3>
            <div className="space-y-2">
              {locationType.locations.map((location: any) => (
                <Link
                  key={location.id}
                  to={`/admin/locations/${location.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{location.name}</p>
                    <p className="text-sm text-gray-500">
                      {location.locationCode || location.slug}
                    </p>
                  </div>
                  {location.isPublic ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Public</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">Private</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Info</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className="font-medium text-gray-900">{locationType.isActive ? 'Active' : 'Inactive'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Last Updated</span>
              <span className="font-medium text-gray-900">{formatDate(locationType.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>}
    </IndividualViewPageLayout>
  );
}



